import { initialBillingData } from 'app/pages/TicketSalePage/slice/initialValues';
import axios from 'axios';
import isEmpty from 'lodash/isEmpty';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Select, { components } from 'react-select';
import { sendBillingAndCustomerInfo } from 'store/cartSlice';
import {
  selectCartData,
  selectTotalPriceOfTopUpsFromCart,
} from 'store/cartSlice/selectors';
import { SendBillingAndCustomerBody } from 'store/cartSlice/types';
import { selectUserData } from 'store/userSlice/selectors';
import { selectVendorFeatures } from 'store/vendorSlice/selectors';
import useEmbedResize from 'utils/custom-hooks/useEmbedResize';
import { debounce } from 'utils/debounce';
import { getCartTotal } from 'utils/getItemsTotal';
import { emailValidationSchema } from 'utils/schemaValidations';
import { API_REQUEST_STATUS_NOT_SENT } from 'utils/static/constants';
import allCountries, { Country } from 'utils/static/countries';
import allStates from 'utils/static/states';
import { checkIfUserIsAuthenticated } from 'utils/users';
import CustomerForm from '../CustomerForm/CustomerForm';
import { OveitIcon } from '../OveitIcon';
import PurchaseOrderField from './GuestTabFields.ts/PurchaseOrderField';
import TaxIdField from './GuestTabFields.ts/TaxIdField';
import {
  addressValidation,
  cityValidation,
  companyNameValidation,
} from './billingInfoValidations';
import { getCustomerFormValues } from './getCustomerFormValues';
import { getDefaultCustomerFormValues } from './getDefaultCustomerFormValues';
import { messages } from './messages';
import { Building, MapPin, User } from 'lucide-react';

const DropdownIndicator = props => (
  <components.DropdownIndicator {...props}>
    <OveitIcon name="arrowDropDown" size={10} className="fill-neutral-600" />
  </components.DropdownIndicator>
);

interface GuestTabContentProps {
  paymentLink: string;
}

const GuestTabContent: React.FC<GuestTabContentProps> = ({ paymentLink }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [requestStatus, setRequestStatus] = useState<string>(
    API_REQUEST_STATUS_NOT_SENT,
  );

  // event & cart data
  const {
    selectedItems,
    selectedEvent,
    selectedEvent: { id: eventId, name: eventName, customerform, type },
    voucher: voucherData,
    uuid: cartUUID,
    billingAndCustomerInfo: { email, billing, fields } = initialBillingData,
  } = useSelector(selectCartData);
  const { cartTotal, feeTotal, taxTotal } = useMemo(
    () => getCartTotal(selectedItems, selectedEvent, voucherData),
    [],
  );
  const totalPriceOfTopUps = useSelector(selectTotalPriceOfTopUpsFromCart);
  const cartIsFree = cartTotal === 0 && totalPriceOfTopUps.sum === 0;

  // vendor
  const { byDefaultOpenCompanyTab, hasEnabledPO } =
    useSelector(selectVendorFeatures);

  // user data
  const {
    email: userEmail,
    name: userName,
    expirationDate,
  } = useSelector(selectUserData);
  const userIsAuthenticated = checkIfUserIsAuthenticated(expirationDate);

  // countries & states
  const [selectedBilling, setSelectedBilling] = useState<
    'individual' | 'company'
  >(byDefaultOpenCompanyTab ? 'company' : 'individual');
  const [selectedCountry, setSelectedCountry] = useState(
    allCountries.find(country => country.id === billing?.address?.id_country) ||
      (allCountries.find(country =>
        i18n.language === 'ro' ? country.id === 141 : country.id === 184,
      ) as Country),
  );
  const [selectedState, setSelectedState] = useState(
    allStates.find(state => state.id === billing?.address?.id_state) || null,
  );
  let availableStates = selectedCountry?.has_states
    ? allStates.filter(({ id_country }) => id_country === selectedCountry.id)
    : [];

  // useEmbedResize([selectedBilling, selectedCountry?.code || '']);

  // default values
  const defaultValuesForCustomerForm = isEmpty(fields)
    ? {}
    : getDefaultCustomerFormValues(fields, cartUUID, customerform);

  const defaultValues = {
    email: userEmail || email || '',
    name: userName || billing?.company || billing?.name || '',
    address: billing?.address?.address || '',
    city: billing?.address?.city || '',
    country: allCountries[billing?.address?.id_country] || 184,
    state: allStates[billing?.address?.id_state] || 0,
    tax_id: billing?.tax_code || '',
    purchaseOrderNumber: billing?.purchaseOrderNumber || '',
    ...defaultValuesForCustomerForm,
  };

  const methods = useForm({ defaultValues });

  const {
    register,
    unregister,
    handleSubmit,
    clearErrors,
    setError,
    reset,
    getValues,
    watch,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (
      checkIfUserIsAuthenticated(expirationDate) === false &&
      getValues('address').length > 0
    ) {
      reset({
        ...defaultValues,
      });
    }
  }, [expirationDate]);

  const handleCountryChange = country => {
    setSelectedCountry(country);
    clearErrors('country');
    if (country.has_states) {
      availableStates = allStates.filter(
        ({ id_country }) => id_country === country.id,
      );
    } else {
      clearErrors('state');
      availableStates = [];
    }
  };

  const handleStateChange = state => {
    clearErrors('state');
    setSelectedState(state);
  };

  const submitForm = async data => {
    if (applyCustomValidations()) {
      return;
    }

    const { email, name, address, city, tax_id, purchaseOrderNumber } = data;
    const requestBody: SendBillingAndCustomerBody = {
      order_id: cartUUID,
      email,
    };
    // if the billing info is not display
    // we should not send the billing object
    if (address) {
      requestBody.billing = {
        type: selectedBilling,
        address: {
          address,
          city,
        },
      };
      if (selectedCountry) {
        requestBody.billing.address.id_country = selectedCountry?.id;
        if (selectedCountry.has_states) {
          requestBody.billing.address.id_state = selectedState?.id;
        }
      }
      if (selectedBilling === 'individual') {
        requestBody.billing.name = name;
      } else {
        requestBody.billing.company = name;
        requestBody.billing.tax_code = tax_id;
        requestBody.billing.purchaseOrderNumber = purchaseOrderNumber;
      }
    }

    if (!isEmpty(customerform)) {
      requestBody.fields = {
        ...getCustomerFormValues(data, customerform, cartUUID),
      };
    }

    const sendData = await dispatch(sendBillingAndCustomerInfo(requestBody));

    if (sendData.error?.message) {
      navigate('/cart/cancel', {
        state: {
          eventId,
          eventName,
          type,
        },
      });
      return;
    }

    navigate('/order/redirect-to-payment-page', {
      state: {
        paymentLink,
        cartTotal:
          cartTotal +
          feeTotal +
          taxTotal +
          totalPriceOfTopUps.sum +
          totalPriceOfTopUps.tax,
      },
    });
  };

  // extra validations for country & state
  const applyCustomValidations = () => {
    if (cartIsFree) return false;

    let encounteredErrors = false;

    if (!selectedCountry) {
      setError('country', {
        type: 'required',
        message: 'requiredField',
      });
      encounteredErrors = true;
    }

    if (availableStates.length > 0 && !selectedState) {
      setError('state', {
        type: 'required',
        message: 'requiredField',
      });
      encounteredErrors = true;
    }

    return encounteredErrors;
  };

  const handleAutoSave = useCallback(async () => {
    const value = getValues();

    if ((await emailValidationSchema.isValid(value.email)) === false) return;

    const data = {
      order_id: cartUUID,
      email: value.email,
      billing: {
        type: selectedBilling,
        name: selectedBilling === 'individual' ? value.name : '',
        company: selectedBilling === 'company' ? value.name : '',
        tax_code: value.tax_id,
        address: {
          address: value.address,
          city: value.city,
          id_country: selectedCountry?.id,
          id_state: selectedState?.id,
        },
      },
    };

    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/cart/save_billing_info`,
        data,
      );
    } catch (error) {}
  }, [selectedCountry?.id, selectedState?.id, selectedBilling]);

  const debouncedAutoSave = useCallback(debounce(handleAutoSave, 3000), [
    handleAutoSave,
  ]);

  // watch for changes in form values
  useEffect(() => {
    const subscription = watch(() => {
      debouncedAutoSave();
    });

    return () => subscription.unsubscribe();
  }, [watch, debouncedAutoSave]);

  // TODO: check if this is needed
  // watch for changes in selectedCountry and selectedState?
  // useEffect(() => {
  //   debouncedAutoSave();
  // }, [selectedCountry, selectedState, debouncedAutoSave]);

  return (
    <FormProvider {...methods}>
      <form
        id="contact-form"
        onSubmit={handleSubmit(submitForm, applyCustomValidations)}
        autoComplete="off"
      >
        <div>
          {cartIsFree ? (
            <>
              <h2 className="title-sm mb-3 sm:mb-6">
                {t(messages.contactInformation())}
              </h2>
              <label className="p3-semibold mandatory" htmlFor="email">
                {t('fields.email')}
              </label>
              <input
                className={`standard-input w-full mt-[4px] ${
                  errors.email?.message && 'error'
                }`}
                type="text"
                readOnly={!!userEmail && userIsAuthenticated}
                {...register('email', {
                  required: {
                    value: true,
                    message: 'requiredField',
                  },
                  validate: {
                    isValid: async value => {
                      const emailIsValid = await emailValidationSchema.isValid(
                        value,
                      );
                      if (!emailIsValid) {
                        return 'validEmailAddress';
                      }
                    },
                  },
                })}
              />
              {errors.email?.message && (
                <p className="standard-input-error">
                  {t(`inputErrors.${errors.email.message}`)}
                </p>
              )}
            </>
          ) : (
            <div>
              <h2 className="title-sm mb-3 sm:mb-6">
                {t(messages.billingInformationTitle())}
              </h2>
              <div className="horizontal-tab-items flex mt-[16px]">
                <div
                  className={`tab-item rounded-l-md ${
                    selectedBilling === 'individual' && 'checked'
                  }`}
                  onClick={() => {
                    setSelectedBilling('individual');
                    clearErrors('tax_id');
                    unregister('tax_id');
                  }}
                >
                  <User />
                  <p className="p2-semibold">{t(messages.individualType())}</p>
                </div>
                <div
                  className={`tab-item rounded-r-md ${
                    selectedBilling === 'company' && 'checked'
                  }`}
                  onClick={() => setSelectedBilling('company')}
                >
                  <Building />
                  <p className="p2-semibold">{t(messages.companyType())}</p>
                </div>
              </div>
              <div className="mt-[16px]">
                <label className="p3-semibold mandatory" htmlFor="email">
                  {t('fields.email')}
                </label>
                <input
                  className={`standard-input w-full mt-[4px] ${
                    errors.email?.message && 'error'
                  }`}
                  type="text"
                  readOnly={!!userEmail && userIsAuthenticated}
                  {...register('email', {
                    required: {
                      value: true,
                      message: 'requiredField',
                    },
                    validate: {
                      isValid: async value => {
                        const emailIsValid =
                          await emailValidationSchema.isValid(value);
                        if (!emailIsValid) {
                          return 'validEmailAddress';
                        }
                      },
                    },
                  })}
                />
                {errors.email?.message && (
                  <p className="standard-input-error">
                    {t(`inputErrors.${errors.email.message}`)}
                  </p>
                )}
              </div>
              {selectedBilling === 'individual' ? (
                <div className="mt-[16px]">
                  <label
                    className="label-input p3-semibold mandatory"
                    htmlFor="name"
                  >
                    {t(messages.name())}
                  </label>
                  <input
                    className={`standard-input w-full ${
                      errors.name?.message && 'error'
                    }`}
                    type="text"
                    {...register('name', {
                      required: {
                        value: true,
                        message: 'requiredField',
                      },
                      minLength: {
                        value: 2,
                        message: 'mustContainAMinimumNumberOfCharacters',
                      },
                    })}
                  />
                  {errors.name?.message && (
                    <p className="standard-input-error">
                      {errors.name.message === 'requiredField'
                        ? t(`inputErrors.${errors.name.message}`)
                        : t(`inputErrors.${errors.name.message}`, {
                            inputName: 'Name',
                            numberOfCharacters: 2,
                          })}
                    </p>
                  )}
                </div>
              ) : null}
              <div className="mt-[16px]">
                <label
                  className="label-input p3-semibold mandatory"
                  htmlFor="country"
                >
                  {t(messages.country())}
                </label>
                <Select
                  placeholder={t(messages.choose())}
                  defaultValue={selectedCountry}
                  onChange={handleCountryChange}
                  getOptionLabel={option => `${option['name']}`}
                  getOptionValue={option => `${option['id']}`}
                  options={allCountries}
                  name={'country'}
                  components={{
                    DropdownIndicator,
                    IndicatorSeparator: undefined,
                  }}
                  classNamePrefix="react-select"
                  className={`${
                    errors.country?.message && 'react-select-error'
                  }`}
                  aria-autocomplete="none"
                />
                {errors.country?.message && (
                  <p className="react-select-error-message">
                    {t(`inputErrors.${errors.country.message}`)}
                  </p>
                )}
              </div>
              {selectedBilling === 'company' ? (
                <>
                  <div className="flex flex-col sm:flex-row gap-[16px] mt-[16px]">
                    <TaxIdField
                      hasEnabledPO={hasEnabledPO}
                      selectedCountry={selectedCountry}
                      setSelectedState={setSelectedState}
                      requestStatus={requestStatus}
                      setRequestStatus={setRequestStatus}
                      selectedBilling={selectedBilling}
                    />
                    {hasEnabledPO ? <PurchaseOrderField /> : null}
                  </div>
                  <div className="mt-[16px]">
                    <label
                      className="label-input p3-semibold mandatory"
                      htmlFor="name"
                    >
                      {t('registrationPage.companyName')}
                    </label>
                    <input
                      className={`standard-input w-full ${
                        errors.name?.message && 'error'
                      }`}
                      type="text"
                      autoComplete="company-name"
                      {...register('name', companyNameValidation)}
                    />
                    {errors.name?.message && (
                      <p className="standard-input-error">
                        {errors.name.message === 'requiredField'
                          ? t(`inputErrors.${errors.name.message}`)
                          : t(`inputErrors.${errors.name.message}`, {
                              inputName: 'Name',
                              numberOfCharacters: 2,
                            })}
                      </p>
                    )}
                  </div>
                </>
              ) : null}
              {!isEmpty(availableStates) && (
                <div className="mt-[16px]">
                  <label
                    className="label-input p3-semibold mandatory"
                    htmlFor="state"
                  >
                    {selectedCountry?.id === 184
                      ? t(messages.state())
                      : t('registrationPage.county')}
                  </label>
                  <Select
                    placeholder={t(messages.choose())}
                    value={selectedState}
                    onChange={handleStateChange}
                    getOptionLabel={option => `${option['name']}`}
                    getOptionValue={option => `${option['id']}`}
                    options={availableStates}
                    name={'state'}
                    components={{
                      DropdownIndicator,
                      IndicatorSeparator: undefined,
                    }}
                    classNamePrefix="react-select"
                    className={`${
                      errors.state?.message && 'react-select-error'
                    }`}
                  />
                  {errors.state?.message && (
                    <p className="react-select-error-message">
                      {t(`inputErrors.${errors.state.message}`)}
                    </p>
                  )}
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-[16px] mt-[16px]">
                <div className="w-full sm:w-1/2">
                  <label
                    className="label-input p3-semibold mandatory"
                    htmlFor="city"
                  >
                    {t(messages.city())}
                  </label>
                  <input
                    className={`standard-input w-full ${
                      errors.city?.message && 'error'
                    }`}
                    type="text"
                    {...register('city', cityValidation)}
                  />
                  {errors.city?.message && (
                    <p className="standard-input-error">
                      {errors.city.message === 'requiredField'
                        ? t(`inputErrors.${errors.city.message}`)
                        : t(`inputErrors.${errors.city.message}`, {
                            inputName: 'City',
                            numberOfCharacters: 2,
                          })}
                    </p>
                  )}
                </div>
                <div className="w-full sm:w-1/2">
                  <label
                    className="label-input p3-semibold mandatory"
                    htmlFor="address"
                  >
                    {t(messages.address())}
                  </label>
                  <div className="relative">
                    <MapPin
                      size={16}
                      className="absolute left-[14px] top-[12px]"
                    />
                    <input
                      className={`standard-input pl-10! w-full ${
                        errors.address?.message ? 'error' : ''
                      }`}
                      type="text"
                      {...register('address', addressValidation)}
                    />
                    {errors.address?.message && (
                      <p className="standard-input-error">
                        {errors.address.message === 'requiredField'
                          ? t(`inputErrors.${errors.address.message}`)
                          : t(`inputErrors.${errors.address.message}`, {
                              inputName: 'Address',
                              numberOfCharacters: 6,
                            })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          <CustomerForm />
        </div>
      </form>
    </FormProvider>
  );
};

export default GuestTabContent;
